import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { StaffAttendanceAPI, CampusGeofenceAPI } from "@/lib/staff-attendance-api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ClipboardCheck, Users, LogIn, LogOut, Loader2, Search,
  CheckCircle2, XCircle, Clock, Save, Calendar, MapPin, AlertCircle,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

const STATUS_OPTS = [
  { v: "present", label: "Present", cls: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20" },
  { v: "absent", label: "Absent", cls: "bg-rose-500/10 text-rose-700 border-rose-500/20" },
  { v: "half_day", label: "Half Day", cls: "bg-amber-500/10 text-amber-700 border-amber-500/20" },
  { v: "on_leave", label: "On Leave", cls: "bg-violet-500/10 text-violet-700 border-violet-500/20" },
  { v: "wfh", label: "WFH", cls: "bg-sky-500/10 text-sky-700 border-sky-500/20" },
  { v: "holiday", label: "Holiday", cls: "bg-zinc-500/10 text-zinc-700 border-zinc-500/20" },
];
const STATUS_MAP = Object.fromEntries(STATUS_OPTS.map((s) => [s.v, s]));

const ADMIN_ROLES = ["super_admin", "tenant_admin", "inst_admin", "campus_admin", "principal", "hr"];

export default function StaffAttendance() {
  const { user } = useAuth();
  const roles = user?.roles || [];
  const isAdmin = roles.some((r) => ADMIN_ROLES.includes(r));

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto py-8 px-4" data-testid="staff-attendance-page">
        <header className="mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center">
              <ClipboardCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display font-extrabold text-2xl tracking-tight">Staff Attendance</h1>
              <p className="text-sm text-muted-foreground">Daily roll-call · self check-in · leave-aware records</p>
            </div>
          </div>
        </header>

        <Tabs defaultValue={isAdmin ? "register" : "me"}>
          <TabsList>
            <TabsTrigger value="me" data-testid="tab-me"><LogIn className="h-3.5 w-3.5 mr-1.5" />Check In / Out</TabsTrigger>
            {isAdmin && <TabsTrigger value="register" data-testid="tab-register"><Users className="h-3.5 w-3.5 mr-1.5" />Daily Register</TabsTrigger>}
          </TabsList>
          <TabsContent value="me" className="mt-6"><MyPanel /></TabsContent>
          {isAdmin && <TabsContent value="register" className="mt-6"><RegisterPanel /></TabsContent>}
        </Tabs>
      </div>
    </AppShell>
  );
}

// ─── Self check-in / out panel ────────────────────────────────────────

const R_EARTH_M = 6_371_000;
function haversineM(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R_EARTH_M * Math.asin(Math.sqrt(h));
}

function useGeolocation() {
  const [state, setState] = useState({ status: "idle", coords: null, error: null });
  const capture = () => {
    if (!("geolocation" in navigator)) {
      setState({ status: "unsupported", coords: null, error: "This browser does not expose location" });
      return;
    }
    setState({ status: "capturing", coords: null, error: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => setState({
        status: "ok",
        coords: {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        },
        error: null,
      }),
      (err) => {
        const map = { 1: "Permission denied", 2: "Position unavailable", 3: "Timed out" };
        setState({ status: "error", coords: null, error: map[err.code] || err.message });
      },
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 },
    );
  };
  return { ...state, capture };
}

function MyPanel() {
  const qc = useQueryClient();
  const meQ = useQuery({ queryKey: ["sa-me-today"], queryFn: () => StaffAttendanceAPI.meToday() });
  const fenceQ = useQuery({ queryKey: ["sa-geofence"], queryFn: () => StaffAttendanceAPI.myGeofence() });
  const geo = useGeolocation();

  // Auto-capture on mount if a fence is configured
  useEffect(() => {
    if (fenceQ.data?.configured && geo.status === "idle") geo.capture();
  }, [fenceQ.data?.configured, geo]);

  const inMut = useMutation({
    mutationFn: () => StaffAttendanceAPI.checkIn(geoPayload()),
    onSuccess: () => { toast.success("Checked in"); qc.invalidateQueries({ queryKey: ["sa-me-today"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const outMut = useMutation({
    mutationFn: () => StaffAttendanceAPI.checkOut(geoPayload()),
    onSuccess: () => { toast.success("Checked out"); qc.invalidateQueries({ queryKey: ["sa-me-today"] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const geoPayload = () => geo.coords ? {
    latitude: geo.coords.latitude,
    longitude: geo.coords.longitude,
    accuracy: geo.coords.accuracy,
  } : { location_error: geo.error || geo.status };

  if (meQ.isLoading) return <Loading />;
  const d = meQ.data;
  if (!d?.has_staff_profile) {
    return <div className="p-8 text-center text-muted-foreground bg-card rounded-2xl" data-testid="sa-me-no-profile">
      Your login isn&apos;t linked to a staff record — so there&apos;s nothing to check in for.
      If you&apos;re an administrator, use the <b>Daily Register</b> tab to mark others; otherwise ask an admin to link your staff profile.
    </div>;
  }
  const rec = d.record;
  const checkedIn = !!rec?.check_in_at;
  const checkedOut = !!rec?.check_out_at;
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—";

  // Distance calculation for the hint
  const fence = fenceQ.data;
  const distance = (fence?.configured && geo.coords)
    ? haversineM({ lat: fence.lat, lng: fence.lng }, { lat: geo.coords.latitude, lng: geo.coords.longitude })
    : null;
  const tolerance = geo.coords?.accuracy || 0;
  const inFence = fence?.configured && distance !== null && distance <= (fence.radius_m + tolerance);
  const canCheckIn = !checkedIn && !inMut.isPending && (
    !fence?.configured
      ? true
      : (!fence.strict || (geo.status === "ok" && inFence))
  );
  const canCheckOut = checkedIn && !checkedOut && !outMut.isPending && (
    !fence?.configured
      ? true
      : (!fence.strict || (geo.status === "ok" && inFence))
  );

  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="sa-me-panel">
      <div className="bg-card rounded-2xl shadow-sm p-6 md:col-span-2">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Today</div>
            <div className="font-display font-bold text-lg mt-0.5">{d.staff_name}</div>
            <div className="text-xs text-muted-foreground">{d.designation || "—"}</div>
          </div>
          {rec && <StatusPill status={rec.status} />}
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <TimeCard label="Check In" time={fmtTime(rec?.check_in_at)} icon={<LogIn className="h-4 w-4" />} tone="emerald" filled={checkedIn} />
          <TimeCard label="Check Out" time={fmtTime(rec?.check_out_at)} icon={<LogOut className="h-4 w-4" />} tone="rose" filled={checkedOut} />
        </div>
        <GeoHint fence={fence} geo={geo} distance={distance} inFence={inFence} onRetry={geo.capture} />
        <div className="flex gap-2 mt-4">
          <Button
            disabled={!canCheckIn}
            onClick={() => inMut.mutate()}
            className="flex-1"
            data-testid="btn-check-in"
          >
            {inMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <LogIn className="h-4 w-4 mr-1.5" />}
            {checkedIn ? "Already checked in" : "Check In"}
          </Button>
          <Button
            variant="outline"
            disabled={!canCheckOut}
            onClick={() => outMut.mutate()}
            className="flex-1"
            data-testid="btn-check-out"
          >
            {outMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <LogOut className="h-4 w-4 mr-1.5" />}
            {checkedOut ? "Already checked out" : "Check Out"}
          </Button>
        </div>
      </div>
      <div className="bg-card rounded-2xl shadow-sm p-6">
        <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Tips</div>
        <ul className="text-sm mt-3 space-y-2 text-muted-foreground">
          <li>• Check-in times are stamped from your device clock.</li>
          <li>• You must allow location and be within your campus radius.</li>
          <li>• Missed a day? Ask your admin to correct it from the Daily Register.</li>
          <li>• Applying for a leave? Head to <a href="/leaves" className="text-primary underline">My Leaves</a>.</li>
        </ul>
      </div>
    </div>
  );
}

function GeoHint({ fence, geo, distance, inFence, onRetry }) {
  if (!fence) return null;
  if (!fence.configured) {
    return (
      <div className="rounded-xl border border-zinc-300 bg-zinc-50 dark:bg-zinc-900 p-3 flex items-start gap-2 text-xs" data-testid="geo-unconfigured">
        <AlertCircle className="h-4 w-4 text-zinc-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-zinc-700 dark:text-zinc-200">Geofence not configured for {fence.campus_name || "your campus"}</div>
          <div className="text-zinc-500 mt-0.5">Ask your campus admin to set the campus location. For now, check-in works from anywhere.</div>
        </div>
      </div>
    );
  }
  if (geo.status === "idle" || geo.status === "capturing") {
    return (
      <div className="rounded-xl border border-sky-300 bg-sky-50 dark:bg-sky-950 p-3 flex items-center gap-2 text-xs" data-testid="geo-capturing">
        <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
        <span>Getting your location…</span>
      </div>
    );
  }
  if (geo.status === "error" || geo.status === "unsupported") {
    return (
      <div className="rounded-xl border border-rose-300 bg-rose-50 dark:bg-rose-950 p-3 flex items-start gap-2 text-xs" data-testid="geo-error">
        <XCircle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-semibold text-rose-700">Location unavailable — {geo.error}</div>
          <div className="text-rose-600 mt-0.5">{fence.strict ? "You cannot check in without location." : "You may check in but it will be flagged for admin review."}</div>
        </div>
        <Button size="sm" variant="outline" className="h-7" onClick={onRetry}>Retry</Button>
      </div>
    );
  }
  const dMeters = Math.round(distance);
  if (inFence) {
    return (
      <div className="rounded-xl border border-emerald-300 bg-emerald-50 dark:bg-emerald-950 p-3 flex items-center gap-2 text-xs" data-testid="geo-in-fence">
        <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
        <div className="flex-1">
          <span className="font-semibold text-emerald-700">You are {dMeters}m from {fence.campus_name}</span>
          <span className="text-emerald-600 ml-1">— within the {fence.radius_m}m allowed radius ✓</span>
        </div>
      </div>
    );
  }
  const away = dMeters > 1000 ? `${(dMeters / 1000).toFixed(1)} km` : `${dMeters}m`;
  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950 p-3 flex items-start gap-2 text-xs" data-testid="geo-out-of-fence">
      <MapPin className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="font-semibold text-amber-700">You are {away} from {fence.campus_name}</div>
        <div className="text-amber-600 mt-0.5">Must be within {fence.radius_m}m of the campus to check in.</div>
      </div>
      <Button size="sm" variant="outline" className="h-7" onClick={onRetry}>Refresh</Button>
    </div>
  );
}

function TimeCard({ label, time, icon, tone, filled }) {
  const filledCls = tone === "emerald" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700" : "bg-rose-500/10 border-rose-500/30 text-rose-700";
  return (
    <div className={`p-4 rounded-xl border ${filled ? filledCls : "bg-muted/40 border-border text-muted-foreground"}`}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold">{icon}{label}</div>
      <div className="font-mono text-xl font-bold mt-1">{time}</div>
    </div>
  );
}

// ─── Daily register + roll-call ────────────────────────────────────────

function RegisterPanel() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(today());
  const [search, setSearch] = useState("");
  const [dept, setDept] = useState("all");
  const [drafts, setDrafts] = useState({}); // staff_id -> {status, notes}
  const [saving, setSaving] = useState(false);
  const [fenceOpen, setFenceOpen] = useState(false);

  const dayQ = useQuery({
    queryKey: ["sa-day", selectedDate],
    queryFn: () => StaffAttendanceAPI.daily({ date: selectedDate }),
  });

  const departments = useMemo(() => {
    const set = new Set((dayQ.data?.rows || []).map((r) => r.department).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [dayQ.data]);

  const filteredRows = useMemo(() => {
    const rows = dayQ.data?.rows || [];
    const q = search.toLowerCase().trim();
    return rows.filter((r) => {
      if (dept !== "all" && r.department !== dept) return false;
      if (q && !(r.name.toLowerCase().includes(q) || (r.employee_id || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [dayQ.data, search, dept]);

  const setDraft = (sid, patch) => setDrafts((prev) => ({ ...prev, [sid]: { ...(prev[sid] || {}), ...patch } }));

  const markAll = (status) => {
    const next = { ...drafts };
    filteredRows.forEach((r) => { next[r.staff_id] = { ...(next[r.staff_id] || {}), status }; });
    setDrafts(next);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const items = Object.entries(drafts)
        .filter(([, v]) => v?.status)
        .map(([staff_id, v]) => ({ staff_id, status: v.status, notes: v.notes || undefined }));
      if (!items.length) throw new Error("Nothing to save");
      return StaffAttendanceAPI.rollcall({ date: selectedDate, items });
    },
    onSuccess: (d) => {
      toast.success(`Marked ${d.marked} staff for ${d.date}`);
      setDrafts({});
      qc.invalidateQueries({ queryKey: ["sa-day"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const draftCount = Object.values(drafts).filter((v) => v?.status).length;
  const counts = dayQ.data?.counts || {};
  const campusId = user?.campus_id;

  return (
    <div className="space-y-4" data-testid="sa-register-panel">
      {/* KPI strip */}
      <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
        <Kpi label="Total" value={dayQ.data?.total ?? 0} tone="default" />
        <Kpi label="Present" value={counts.present || 0} tone="emerald" />
        <Kpi label="Absent" value={counts.absent || 0} tone="rose" />
        <Kpi label="Half Day" value={counts.half_day || 0} tone="amber" />
        <Kpi label="On Leave" value={counts.on_leave || 0} tone="violet" />
        <Kpi label="WFH" value={counts.wfh || 0} tone="sky" />
        <Kpi label="Unmarked" value={counts.unmarked || 0} tone="zinc" />
      </div>

      {campusId && (
        <div className="flex items-center justify-end">
          <Button variant="ghost" size="sm" className="text-xs" onClick={() => setFenceOpen(true)} data-testid="btn-open-geofence">
            <MapPin className="h-3.5 w-3.5 mr-1" /> Configure Campus Geofence
          </Button>
        </div>
      )}

      {fenceOpen && campusId && <GeofenceDialog campusId={campusId} onClose={() => setFenceOpen(false)} />}

      {/* Controls */}
      <div className="bg-card rounded-2xl shadow-sm p-4 flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <Input
            type="date"
            value={selectedDate}
            onChange={(e) => { setSelectedDate(e.target.value); setDrafts({}); }}
            className="w-44"
            data-testid="sa-date-picker"
          />
        </div>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name or employee id" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="sa-search" />
        </div>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger className="w-44" data-testid="sa-dept-filter"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>{departments.map((d) => <SelectItem key={d} value={d}>{d === "all" ? "All Departments" : d}</SelectItem>)}</SelectContent>
        </Select>
        <div className="hidden md:block h-6 w-px bg-border" />
        <div className="text-xs text-muted-foreground">Quick mark all filtered:</div>
        <div className="flex gap-1">
          {STATUS_OPTS.slice(0, 4).map((s) => (
            <Button key={s.v} size="sm" variant="outline" className="h-8" onClick={() => markAll(s.v)} data-testid={`sa-quickmark-${s.v}`}>{s.label}</Button>
          ))}
        </div>
        <Button
          className="ml-auto"
          disabled={draftCount === 0 || saveMut.isPending}
          onClick={() => saveMut.mutate()}
          data-testid="sa-save"
        >
          {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save {draftCount ? `(${draftCount})` : ""}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-card rounded-2xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Staff</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>Current</TableHead>
              <TableHead className="w-56">Mark</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dayQ.isLoading && <TableRow><TableCell colSpan={5} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>}
            {!dayQ.isLoading && filteredRows.map((r) => (
              <TableRow key={r.staff_id} data-testid={`sa-row-${r.staff_id}`}>
                <TableCell>
                  <div className="font-medium">{r.name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{r.employee_id || "—"}</div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.department || "—"}</TableCell>
                <TableCell className="text-sm">{r.designation || "—"}</TableCell>
                <TableCell><StatusPill status={r.status} /></TableCell>
                <TableCell>
                  <Select
                    value={drafts[r.staff_id]?.status || ""}
                    onValueChange={(v) => setDraft(r.staff_id, { status: v })}
                  >
                    <SelectTrigger className="h-8" data-testid={`sa-mark-${r.staff_id}`}>
                      <SelectValue placeholder="Change to…" />
                    </SelectTrigger>
                    <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
            {!dayQ.isLoading && filteredRows.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No staff match your filter.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  if (!status || status === "unmarked") return <Badge variant="outline" className="text-xs">Unmarked</Badge>;
  const s = STATUS_MAP[status];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${s?.cls || ""}`}>{s?.label || status}</span>;
}

function Kpi({ label, value, tone }) {
  const cls = {
    emerald: "text-emerald-600", rose: "text-rose-600", amber: "text-amber-600",
    violet: "text-violet-600", sky: "text-sky-600", zinc: "text-zinc-600", default: "",
  }[tone] || "";
  return (
    <div className="bg-card rounded-xl shadow-sm p-3">
      <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
      <div className={`font-display font-extrabold text-2xl tracking-tight mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

function Loading() {
  return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>;
}

// ─── Geofence Admin Dialog ────────────────────────────────────────────

function GeofenceDialog({ campusId, onClose }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["campus-geofence", campusId], queryFn: () => CampusGeofenceAPI.get(campusId) });
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState(200);
  const [strict, setStrict] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.data) {
      setLat(q.data.lat ?? "");
      setLng(q.data.lng ?? "");
      setRadius(q.data.radius_m || 200);
      setStrict(q.data.strict !== false);
    }
  }, [q.data]);

  const useMyLocation = () => {
    if (!("geolocation" in navigator)) return toast.error("Location not supported");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
        setBusy(false);
        toast.success(`Pinned at ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)}`);
      },
      (err) => { setBusy(false); toast.error("Could not get location: " + err.message); },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const saveMut = useMutation({
    mutationFn: () => CampusGeofenceAPI.update(campusId, {
      lat: parseFloat(lat), lng: parseFloat(lng), radius_m: Number(radius), strict,
    }),
    onSuccess: () => {
      toast.success("Geofence updated");
      qc.invalidateQueries({ queryKey: ["campus-geofence", campusId] });
      qc.invalidateQueries({ queryKey: ["sa-geofence"] });
      onClose();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const clearMut = useMutation({
    mutationFn: () => CampusGeofenceAPI.update(campusId, { clear: true }),
    onSuccess: () => {
      toast.success("Geofence cleared");
      qc.invalidateQueries({ queryKey: ["campus-geofence", campusId] });
      qc.invalidateQueries({ queryKey: ["sa-geofence"] });
      onClose();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="geofence-dialog">
        <DialogHeader><DialogTitle>Campus Geofence — {q.data?.campus_name || "..."}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Latitude</label>
              <Input type="number" step="0.00001" value={lat} onChange={(e) => setLat(e.target.value)} data-testid="fence-lat" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">Longitude</label>
              <Input type="number" step="0.00001" value={lng} onChange={(e) => setLng(e.target.value)} data-testid="fence-lng" />
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full" onClick={useMyLocation} disabled={busy} data-testid="fence-use-mine">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <MapPin className="h-4 w-4 mr-1.5" />}
            Use my current location as campus centre
          </Button>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Radius (metres) — {radius}m</label>
            <Input type="range" min="25" max="5000" step="25" value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="fence-radius" />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>25m (strict)</span><span>5000m (loose)</span>
            </div>
          </div>
          <div className="flex items-center justify-between bg-muted/40 rounded-lg p-3">
            <div>
              <div className="text-sm font-semibold">Strict enforcement</div>
              <div className="text-xs text-muted-foreground">Block check-in when outside the fence or location denied</div>
            </div>
            <Switch checked={strict} onCheckedChange={setStrict} data-testid="fence-strict" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {q.data?.configured && (
            <Button variant="ghost" className="text-rose-600 mr-auto" onClick={() => clearMut.mutate()} disabled={clearMut.isPending} data-testid="fence-clear">Clear geofence</Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!lat || !lng || saveMut.isPending} onClick={() => saveMut.mutate()} data-testid="fence-save">
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Save geofence
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
